import { toolQuery } from "@common/api/tools"
import type { ToolDetailPublic } from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { useEscape } from "@common/hooks/use-escape"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useMatch,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { Tag } from "lucide-react"
import { DetailPane, PaneMenu } from "@/components/layout/detail-panel"
import { PaneSections, PaneStepper } from "@/components/page/pane"
import { AttachmentsSection } from "../-components/sections/attachments"
import { CalibrationSection } from "../-components/sections/calibration"
import {
  CurrentLoan,
  DetailsFields,
  DetailsForm,
  RetireItem,
} from "../-components/sections/details"
import { HistorySection } from "../-components/sections/history"
import { LoansSection } from "../-components/sections/loans"
import { ToolHeading, ToolSummary } from "../-components/tool-heading"
import { useOrderedTools } from "../-components/tool-table"
import { paneSearchSchema, type Section, sectionTitle } from "../-lib/pane"

export const Route = createFileRoute("/_layout/tools/$toolId")({
  staticData: { detailSidebar: true },
  validateSearch: (search) => paneSearchSchema.parse(search),
  loader: ({ context: { queryClient }, params: { toolId } }) =>
    queryClient.ensureQueryData(toolQuery({ tool_id: toolId })),
  component: ToolPanel,
})

function useActing(): Section | null {
  const options = { shouldThrow: false } as const
  const issuing = useMatch({ from: "/_layout/tools/$toolId/issue", ...options })
  const receiving = useMatch({
    from: "/_layout/tools/$toolId/loans/$loanId/receive",
    ...options,
  })
  const sending = useMatch({
    from: "/_layout/tools/$toolId/calibrations/send",
    ...options,
  })
  const recording = useMatch({
    from: "/_layout/tools/$toolId/calibrations/record",
    ...options,
  })
  const completing = useMatch({
    from: "/_layout/tools/$toolId/calibrations/$calibrationId/complete",
    ...options,
  })
  if (issuing || receiving) return "loans"
  if (sending || recording || completing) return "calibration"
  return null
}

function ToolPanel(): React.ReactElement {
  const { toolId } = Route.useParams()
  const { section } = Route.useSearch()
  const { data: tool } = useSuspenseQuery(toolQuery({ tool_id: toolId }))
  const navigate = useNavigate()
  const acting = useActing()
  const listSearch = useSearch({ from: "/_layout/tools" })
  const close = () => navigate({ to: "/tools", search: listSearch })
  useEscape(close)

  const clearSection = () => {
    if (!section) return
    void navigate({
      to: ".",
      search: (previous) => ({ ...previous, section: undefined }),
      replace: true,
    })
  }

  return (
    <DetailPane
      title={tool.number}
      heading={<ToolHeading tool={tool} />}
      actions={<PaneActions tool={tool} />}
      onClose={close}
    >
      <div className="flex h-full flex-col">
        <DetailsForm key={tool.id} tool={tool}>
          <PaneSections
            header={<ToolSummary tool={tool} />}
            jump={sectionTitle(acting ?? section ?? "details")}
            onJump={clearSection}
          >
            <CurrentLoan tool={tool} />
            <DetailsFields tool={tool} />
            <CalibrationSection tool={tool}>
              {acting === "calibration" && <Outlet />}
            </CalibrationSection>
            {tool.classification === "calibration_controlled" && (
              <HistorySection tool={tool} />
            )}
            <LoansSection tool={tool}>
              {acting === "loans" && <Outlet />}
            </LoansSection>
            <AttachmentsSection tool={tool} />
          </PaneSections>
          {!acting && <Outlet />}
        </DetailsForm>
      </div>
    </DetailPane>
  )
}

function PaneActions({ tool }: { tool: ToolDetailPublic }): React.ReactElement {
  const navigate = useNavigate()
  const listSearch = useSearch({ from: "/_layout/tools" })
  const ordered = useOrderedTools(listSearch)
  const index = ordered.findIndex((candidate) => candidate.id === tool.id)
  const goTo = (offset: number) => {
    const next = ordered[index + offset]
    return next && index >= 0
      ? () =>
          navigate({
            to: "/tools/$toolId",
            params: { toolId: next.id },
            search: true,
          })
      : null
  }

  return (
    <>
      <PaneStepper
        index={index}
        total={ordered.length}
        noun="tool"
        onPrevious={goTo(-1)}
        onNext={goTo(1)}
      />
      <PaneMenu label="Tool actions">
        <DropdownMenuItem
          onSelect={() =>
            navigate({
              to: "/tools/$toolId/label",
              params: { toolId: tool.id },
              search: true,
            })
          }
        >
          <Tag />
          Print label
        </DropdownMenuItem>
        <RetireItem tool={tool} />
      </PaneMenu>
    </>
  )
}
